// Import the PURE transcript helper from its own subpath, NOT the `/services` barrel — that barrel also
// re-exports server-only services (AspectsManager → @wix/wix-aspects → node:async_hooks), which webpack
// then tries to resolve into the browser bundle and fails ("Can't resolve 'async_hooks'"). This is client
// code, so it must reach `sanitizeAgentText` without dragging the server graph in.
import { sanitizeAgentText } from '@wix/ai-scheduling-assistant-common-lib/agentActionLine';
import { bucketContaining } from '../../../lib/agent-task-buckets';
import type { DuplexerEntity } from '../../../lib/use-duplexer';

// The pure fold behind the "View agent conversation" transcript (AgentConversationPanel): how the worker
// agent's raw messages become the chat timeline it reads like. Kept out of the component so it is unit
// tested directly, and mirrored 1:1 by the native ports (iOS/Android `buildRows`).

export interface TranscriptMessage {
  id?: string;
  role?: string;
  content?: string;
  // Server-computed human sentence for a TOOL_USE row; raw tool name/params never reach the client.
  actionLine?: string;
  agentId?: string;
  createdDate?: string;
}

export interface TranscriptResponse {
  // The worker conversation this transcript belongs to — the id the live duplexer stream is filtered on.
  agentConversationId?: string;
  messages?: TranscriptMessage[];
  reviewerMessages?: TranscriptMessage[];
  reviewerUnavailable?: boolean;
  status?: string;
}

// A settled row of the transcript: the agent's own step narration ('narrate' — "let me check the brain
// for Avishai's contact"), its final spoken turn ('say'), or a muted action beat ('act').
export type Row = { kind: 'say' | 'narrate' | 'act'; text: string; agentId?: string; time?: string };

export function isTerminal(status?: string): boolean {
  const bucket = bucketContaining(status);
  return bucket === 'done' || bucket === 'failed';
}

export function clock(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// Fold the raw messages into the chat timeline. The agent's own words carry the story, so its step
// narration (ASSISTANT_STEP) and its final turn (ASSISTANT) both show — sanitized, since the agent
// sometimes appends a raw id ("…sent. outreach_id: `…`") to a line meant for a person. A TOOL_USE's
// server-computed action line is a supporting beat; consecutive identical beats ("Reading the notes"
// five times in a row) collapse to one. Everything else (results, thinking, the seed) drops.
export function buildRows(messages: TranscriptMessage[]): Row[] {
  const rows: Row[] = [];
  for (const m of messages) {
    const time = clock(m.createdDate);
    if (m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP') {
      const text = sanitizeAgentText(m.content);
      if (text) rows.push({ kind: m.role === 'ASSISTANT' ? 'say' : 'narrate', text, agentId: m.agentId, time });
    } else if (m.actionLine) {
      const prev = rows[rows.length - 1];
      if (prev?.kind === 'act' && prev.text === m.actionLine) continue; // collapse a run of the same beat
      rows.push({ kind: 'act', text: m.actionLine, agentId: m.agentId, time });
    }
  }
  return rows;
}

// Collapse a run of the same beat, for a caller that has REMOVED rows since `buildRows` folded them.
// `buildRows` only ever sees beats in their original order, so two identical beats separated by a
// narration survive it — and the at-work card, which drops narrations once a plan is on screen, is then
// left with them adjacent ("Reading the notes" three times under the checklist). Mirrored 1:1 by the
// native ports.
export function collapseRepeatedActs(rows: Row[]): Row[] {
  return rows.filter((row, i) => !(row.kind === 'act' && rows[i - 1]?.kind === 'act' && rows[i - 1].text === row.text));
}

// The rows the in-chat at-work card shows for the WORKER side, from `buildRows`' fold.
//
// Two drops, for different reasons:
//  - 'say' ALWAYS. A sub-agent's seed opens "There is no user in this conversation", and the gateway
//    persists model text as ASSISTANT only when that block carried NO tool call
//    (`streaming-agentic-turn`'s `finalRole`). So in the worker's own thread a 'say' row is the
//    loop-ender it writes because a tool result told it to "produce a brief final text turn and stop" —
//    a paragraph addressed to nobody, whose content the task already carries as its deliverable. The
//    task screen's full transcript (AgentConversationPanel) still shows it; the CARD is a window on the
//    WORK.
//  - 'narrate' only when a plan is on screen, which already renders those steps as the checklist. With
//    no plan the card's transcript IS the checklist, so narrations stay.
//
// Mirrored 1:1 by the native ports (iOS `AgentConversationBody.workerCardRows`, Android `workerCardRows`).
export function workerCardRows(rows: Row[], hasPlan: boolean): Row[] {
  return collapseRepeatedActs(rows.filter((r) => r.kind !== 'say' && !(hasPlan && r.kind === 'narrate')));
}

// A live duplexer push (a worker ASSISTANT / ASSISTANT_STEP / TOOL_USE row) folded into the transcript's
// message shape, or null for anything the transcript doesn't render. The authoring agent rides in
// `metadata.agentId`.
export function entityToMessage(entity: DuplexerEntity): TranscriptMessage | null {
  const agentId = (entity.metadata as { agentId?: string } | undefined)?.agentId;
  if ((entity.role === 'ASSISTANT' || entity.role === 'ASSISTANT_STEP') && entity.content?.trim()) {
    return { id: entity.id, role: entity.role, content: entity.content, agentId, createdDate: entity.createdDate };
  }
  if (entity.role === 'TOOL_USE' && entity.actionLine) {
    return { id: entity.id, role: entity.role, actionLine: entity.actionLine, agentId, createdDate: entity.createdDate };
  }
  return null;
}
